import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { isRelease150 } from "../lib/release150";

interface State {
  error: Error | null;
}

/** Nothing on a shop floor should be a white screen. A render failure says what happened and offers a way back. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Summit Operations: a page failed to render", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto mt-16 max-w-xl px-4">
        <div role="alert" className="card border-red-200 !p-5">
          <p className="inline-flex items-center gap-2 text-sm font-semibold text-red-700">
            <AlertTriangle aria-hidden="true" className="h-4 w-4" />
            This page could not be drawn
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Nothing was changed. Reload the page; if it keeps happening, tell the Summit Operations administrator what you were doing.
          </p>
          {/* Release 1.5.0 (O-R68): the raw exception text is for the console log, not for the person. */}
          {!isRelease150() && <p className="mt-2 font-mono text-xs text-slate-500">{this.state.error.message}</p>}
          <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
